import { motion } from 'framer-motion';
import {
    ArrowRight,
    Award,
    Building2,
    CheckCircle,
    GraduationCap,
    Key,
    Server,
    Shield,
    User,
    Users,
    Zap
} from 'lucide-react';
import { Link } from 'react-router-dom';

const TechPlatform = () => {

    return (
        <div className="min-h-screen pt-20">
            {/* Hero Section */}
            <section className="relative overflow-hidden py-20 bg-surface-100">
                <div className="container-custom text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <h1 className="text-4xl lg:text-6xl font-bold mb-6 relative">
                            <span className="bg-gradient-to-r from-white via-gray-100 to-white bg-clip-text text-transparent drop-shadow-lg">
                                The $127 Billion
                            </span>
                            <br />
                            <span className="bg-gradient-to-r from-neon-300 via-neon-400 to-primary-400 bg-clip-text text-transparent animate-pulse drop-shadow-2xl">
                                Identity Revolution
                            </span>
                            <div className="absolute -inset-2 bg-gradient-to-r from-neon-600/10 via-primary-600/10 to-neon-600/10 blur-xl -z-10 rounded-lg"></div>
                        </h1>
                        <p className="text-xl text-gray-300 max-w-4xl mx-auto mb-8">
                            <strong className="text-white">Documint is positioned to capture the exploding digital identity market</strong> — projected to reach $127B by 2030. 
                            Our blockchain-anchored SSI platform eliminates the $15B annual cost of identity fraud while creating new revenue streams 
                            for enterprises across finance, healthcare, education, and government sectors.
                        </p>
                        <Link to="/pricing" className="btn-primary inline-flex items-center">
                            Join the Revolution
                            <ArrowRight size={20} className="ml-2" />
                        </Link>
                    </motion.div>
                </div>
            </section>

            {/* Self-Sovereign Digital Identity Section */}
            <section className="py-20 bg-surface-200/40">
                <div className="container-custom">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-4xl lg:text-5xl font-bold mb-6 relative">
                            <span className="bg-gradient-to-r from-white via-gray-100 to-white bg-clip-text text-transparent drop-shadow-lg">
                                Your Digital
                            </span>
                            <span className="text-neon-300"> &ldquo;ME&rdquo;</span>
                            <br />
                            <span className="bg-gradient-to-r from-neon-300 via-neon-400 to-primary-400 bg-clip-text text-transparent">
                                Self-Sovereign Identity Revolution
                            </span>
                        </h2>
                        <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
                            Welcome to the future where <strong className="text-white">you are the ultimate authority</strong> over your digital existence. 
                            No corporations, no governments, no intermediaries — just pure, uncompromising digital sovereignty.
                        </p>
                    </motion.div>

                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                        >
                            <div className="relative">
                                <div className="absolute -inset-4 bg-gradient-to-r from-primary-600/30 to-neon-600/30 rounded-3xl blur-2xl"></div>
                                <div className="relative glass p-10 rounded-3xl border border-white/20">
                                    <div className="text-center mb-8">
                                        <div className="w-24 h-24 bg-gradient-to-br from-primary-600 via-neon-500 to-primary-400 rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl">
                                            <div className="w-20 h-20 bg-gradient-to-br from-white/20 to-transparent rounded-full flex items-center justify-center">
                                                <User className="w-12 h-12 text-white drop-shadow-lg" />
                                            </div>
                                        </div>
                                        <h3 className="text-2xl font-bold text-white mb-3">The Decentralized Identifier (DID)</h3>
                                        <p className="text-neon-300 font-medium">Your Cryptographic Fingerprint</p>
                                    </div>
                                    
                                    <div className="space-y-6">
                                        <div className="bg-gradient-to-r from-surface-100/40 to-surface-200/40 rounded-xl p-5 border border-neon-300/20">
                                            <div className="flex items-center justify-between mb-3">
                                                <span className="font-semibold text-white flex items-center">
                                                    <Shield className="w-5 h-5 text-green-400 mr-2" />
                                                    Identity Sovereignty
                                                </span>
                                                <span className="text-green-400 text-sm font-bold bg-green-400/20 px-3 py-1 rounded-full">100% Yours</span>
                                            </div>
                                            <div className="text-sm text-gray-300">Mathematically provable ownership with zero external dependencies</div>
                                        </div>
                                        
                                        <div className="bg-gradient-to-r from-surface-100/40 to-surface-200/40 rounded-xl p-5 border border-primary-300/20">
                                            <div className="flex items-center justify-between mb-3">
                                                <span className="font-semibold text-white flex items-center">
                                                    <Key className="w-5 h-5 text-blue-400 mr-2" />
                                                    Cryptographic Privacy
                                                </span>
                                                <span className="text-blue-400 text-sm font-bold">Zero-Knowledge</span>
                                            </div>
                                            <div className="text-sm text-gray-300">Selective disclosure with military-grade encryption standards</div>
                                        </div>
                                        
                                        <div className="bg-gradient-to-r from-surface-100/40 to-surface-200/40 rounded-xl p-5 border border-purple-300/20">
                                            <div className="flex items-center justify-between mb-3">
                                                <span className="font-semibold text-white flex items-center">
                                                    <Zap className="w-5 h-5 text-purple-400 mr-2" />
                                                    Quantum-Ready Security
                                                </span>
                                                <span className="text-purple-400 text-sm font-bold">Future-Proof</span>
                                            </div>
                                            <div className="text-sm text-gray-300">Resistant to quantum computing attacks with post-quantum cryptography</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6, delay: 0.4 }}
                            className="space-y-8"
                        >
                            <div>
                                <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
                                    <div className="w-3 h-8 bg-gradient-to-b from-neon-400 to-primary-500 rounded-full mr-4"></div>
                                    The Identity Paradigm Shift
                                </h3>
                                <p className="text-lg text-gray-300 leading-relaxed mb-6">
                                    For decades, digital identity has been controlled by centralized authorities — tech giants, governments, and institutions that monetize your data while limiting your freedom. <strong className="text-white">Self-Sovereign Identity shatters this paradigm.</strong>
                                </p>
                                <p className="text-lg text-gray-300 leading-relaxed">
                                    With blockchain-anchored <strong className="text-neon-300">Decentralized Identifiers (DIDs)</strong>, you become the issuer, holder, and verifier of your own identity. No permission needed, no central point of failure, no surveillance capitalism.
                                </p>
                            </div>

                            <div className="grid md:grid-cols-2 gap-6">
                                <div className="group">
                                    <div className="flex items-start space-x-4 p-4 rounded-xl bg-gradient-to-r from-green-500/10 to-green-600/10 border border-green-400/20 group-hover:border-green-400/40 transition-all duration-300">
                                        <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                                            <CheckCircle className="w-6 h-6 text-green-400" />
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-white mb-2">Absolute Ownership</h4>
                                            <p className="text-sm text-gray-400">Your identity, your keys, your rules. No backdoors, no exceptions.</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="group">
                                    <div className="flex items-start space-x-4 p-4 rounded-xl bg-gradient-to-r from-blue-500/10 to-blue-600/10 border border-blue-400/20 group-hover:border-blue-400/40 transition-all duration-300">
                                        <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                                            <Server className="w-6 h-6 text-blue-400" />
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-white mb-2">Peer-to-Peer Trust</h4>
                                            <p className="text-sm text-gray-400">Direct verification without intermediaries or central authorities.</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="group">
                                    <div className="flex items-start space-x-4 p-4 rounded-xl bg-gradient-to-r from-purple-500/10 to-purple-600/10 border border-purple-400/20 group-hover:border-purple-400/40 transition-all duration-300">
                                        <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                                            <Shield className="w-6 h-6 text-purple-400" />
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-white mb-2">Privacy by Design</h4>
                                            <p className="text-sm text-gray-400">Share only what you choose, when you choose, with whom you choose.</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="group">
                                    <div className="flex items-start space-x-4 p-4 rounded-xl bg-gradient-to-r from-orange-500/10 to-orange-600/10 border border-orange-400/20 group-hover:border-orange-400/40 transition-all duration-300">
                                        <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                                            <Zap className="w-6 h-6 text-orange-400" />
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-white mb-2">Global Interoperability</h4>
                                            <p className="text-sm text-gray-400">One identity that works everywhere, across all platforms and services.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gradient-to-r from-primary-600/20 to-neon-600/20 rounded-2xl p-6 border border-neon-300/30">
                                <h4 className="text-xl font-bold text-neon-300 mb-3">The Technical Reality</h4>
                                <p className="text-gray-300 leading-relaxed">
                                    Every DID is a unique cryptographic identifier anchored on immutable blockchain networks. It&apos;s mathematically impossible to fake, duplicate, or steal — representing the most secure form of digital identity ever created.
                                </p>
                            </div>

                            <Link to="/pricing" className="btn-primary inline-flex items-center text-lg px-8 py-4">
                                Claim Your Digital Sovereignty
                                <ArrowRight size={24} className="ml-3" />
                            </Link>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* DID Global Network Section */}
            <section className="py-20 bg-surface-100">
                <div className="container-custom">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
                            <span className="text-neon-300">DID</span> Global Network
                            <br />
                            <span className="bg-gradient-to-r from-primary-300 to-neon-400 bg-clip-text text-transparent">
                                Where Trust Lives
                            </span>
                        </h2>
                        <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
                            Your Decentralized Identifier doesn&apos;t live in isolation — it&apos;s part of a <strong className="text-white">global trust infrastructure</strong> spanning 
                            multiple blockchain networks, enterprise systems, and verification ecosystems worldwide.
                        </p>
                    </motion.div>

                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                        >
                            {/* Network Visualization */}
                            <div className="relative">
                                <div className="absolute -inset-8 bg-gradient-to-r from-neon-600/20 via-primary-600/20 to-neon-600/20 rounded-3xl blur-2xl"></div>
                                <div className="relative glass p-8 rounded-3xl border border-neon-300/30">
                                    <div className="text-center mb-8">
                                        <h3 className="text-2xl font-bold text-white mb-3">DID Association Map</h3>
                                        <p className="text-neon-300 font-medium">Your Identity Across Networks</p>
                                    </div>

                                    {/* Central DID Node */}
                                    <div className="relative flex items-center justify-center mb-8">
                                        <div className="absolute inset-0 bg-gradient-to-r from-neon-400/30 to-primary-400/30 rounded-full blur-xl animate-pulse"></div>
                                        <div className="relative w-24 h-24 bg-gradient-to-br from-neon-500 to-primary-500 rounded-full flex items-center justify-center shadow-2xl">
                                            <Key className="w-12 h-12 text-white" />
                                        </div>
                                        <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                                            <CheckCircle className="w-4 h-4 text-white" />
                                        </div>
                                    </div>

                                    {/* Network Connections */}
                                    <div className="grid grid-cols-2 gap-6">
                                        {/* Blockchain Networks */}
                                        <div className="space-y-4">
                                            <div className="flex items-center space-x-3 p-3 bg-surface-200/30 rounded-lg border border-blue-400/20">
                                                <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center">
                                                    <Server className="w-5 h-5 text-blue-400" />
                                                </div>
                                                <div>
                                                    <div className="text-sm font-semibold text-white">Ethereum</div>
                                                    <div className="text-xs text-gray-400">Mainnet Anchor</div>
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-3 p-3 bg-surface-200/30 rounded-lg border border-purple-400/20">
                                                <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center">
                                                    <Zap className="w-5 h-5 text-purple-400" />
                                                </div>
                                                <div>
                                                    <div className="text-sm font-semibold text-white">Algorand</div>
                                                    <div className="text-xs text-gray-400">Pure PoS Chain</div>
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-3 p-3 bg-surface-200/30 rounded-lg border border-green-400/20">
                                                <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center">
                                                    <Shield className="w-5 h-5 text-green-400" />
                                                </div>
                                                <div>
                                                    <div className="text-sm font-semibold text-white">Hyperledger</div>
                                                    <div className="text-xs text-gray-400">Enterprise Grade</div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Trust Registries */}
                                        <div className="space-y-4">
                                            <div className="flex items-center space-x-3 p-3 bg-surface-200/30 rounded-lg border border-orange-400/20">
                                                <div className="w-8 h-8 bg-orange-500/20 rounded-lg flex items-center justify-center">
                                                    <Building2 className="w-5 h-5 text-orange-400" />
                                                </div>
                                                <div>
                                                    <div className="text-sm font-semibold text-white">Gov Registries</div>
                                                    <div className="text-xs text-gray-400">Official Bodies</div>
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-3 p-3 bg-surface-200/30 rounded-lg border border-cyan-400/20">
                                                <div className="w-8 h-8 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                                                    <GraduationCap className="w-5 h-5 text-cyan-400" />
                                                </div>
                                                <div>
                                                    <div className="text-sm font-semibold text-white">Universities</div>
                                                    <div className="text-xs text-gray-400">Academic Network</div>
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-3 p-3 bg-surface-200/30 rounded-lg border border-pink-400/20">
                                                <div className="w-8 h-8 bg-pink-500/20 rounded-lg flex items-center justify-center">
                                                    <Users className="w-5 h-5 text-pink-400" />
                                                </div>
                                                <div>
                                                    <div className="text-sm font-semibold text-white">Enterprises</div>
                                                    <div className="text-xs text-gray-400">Corporate Trust</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Connection Lines Simulation */}
                                    <div className="absolute inset-0 pointer-events-none">
                                        <div className="absolute top-1/2 left-1/2 w-px h-8 bg-gradient-to-b from-neon-400 to-transparent transform -translate-x-1/2 -translate-y-4"></div>
                                        <div className="absolute top-1/2 left-1/2 w-8 h-px bg-gradient-to-r from-neon-400 to-transparent transform -translate-x-4 -translate-y-1/2"></div>
                                        <div className="absolute top-1/2 left-1/2 w-px h-8 bg-gradient-to-t from-neon-400 to-transparent transform -translate-x-1/2 translate-y-4"></div>
                                        <div className="absolute top-1/2 left-1/2 w-8 h-px bg-gradient-to-l from-neon-400 to-transparent transform translate-x-4 -translate-y-1/2"></div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6, delay: 0.4 }}
                            className="space-y-8"
                        >
                            <div>
                                <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
                                    <div className="w-3 h-8 bg-gradient-to-b from-neon-400 to-primary-500 rounded-full mr-4"></div>
                                    Universal Trust Infrastructure
                                </h3>
                                <p className="text-lg text-gray-300 leading-relaxed mb-6">
                                    Your DID isn&apos;t just a random string of characters — it&apos;s a <strong className="text-white">cryptographically secured address</strong> that connects you to a vast network of trusted entities across multiple dimensions of digital society.
                                </p>
                                <p className="text-lg text-gray-300 leading-relaxed">
                                    From <strong className="text-neon-300">Fortune 500 companies</strong> to <strong className="text-primary-300">government agencies</strong>, from <strong className="text-blue-300">top universities</strong> to <strong className="text-green-300">healthcare systems</strong> — your DID creates verifiable relationships with the institutions that matter most.
                                </p>
                            </div>

                            {/* Association Categories */}
                            <div className="grid gap-6">
                                <div className="bg-gradient-to-r from-blue-600/20 to-cyan-600/20 rounded-2xl p-6 border border-blue-400/30">
                                    <h4 className="text-xl font-bold text-blue-300 mb-4 flex items-center">
                                        <Server className="w-6 h-6 mr-3" />
                                        Blockchain Networks
                                    </h4>
                                    <p className="text-gray-300 leading-relaxed mb-4">
                                        Your DID is permanently anchored across <strong>15+ blockchain networks</strong> including Ethereum, Algorand, Hyperledger Fabric, and Polygon — ensuring global accessibility and impossible-to-forge provenance.
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm font-medium">Ethereum Mainnet</span>
                                        <span className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm font-medium">Algorand</span>
                                        <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm font-medium">Hyperledger</span>
                                        <span className="px-3 py-1 bg-orange-500/20 text-orange-300 rounded-full text-sm font-medium">Polygon</span>
                                    </div>
                                </div>

                                <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-2xl p-6 border border-purple-400/30">
                                    <h4 className="text-xl font-bold text-purple-300 mb-4 flex items-center">
                                        <Building2 className="w-6 h-6 mr-3" />
                                        Enterprise Trust Registries
                                    </h4>
                                    <p className="text-gray-300 leading-relaxed mb-4">
                                        Connected to <strong>500+ enterprise trust registries</strong> worldwide, including government identity systems, corporate HR platforms, educational institutions, and healthcare networks.
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        <span className="px-3 py-1 bg-red-500/20 text-red-300 rounded-full text-sm font-medium">Government IDs</span>
                                        <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm font-medium">Universities</span>
                                        <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm font-medium">Hospitals</span>
                                        <span className="px-3 py-1 bg-yellow-500/20 text-yellow-300 rounded-full text-sm font-medium">Corporations</span>
                                    </div>
                                </div>

                                <div className="bg-gradient-to-r from-green-600/20 to-emerald-600/20 rounded-2xl p-6 border border-green-400/30">
                                    <h4 className="text-xl font-bold text-green-300 mb-4 flex items-center">
                                        <Users className="w-6 h-6 mr-3" />
                                        Global Verification Network
                                    </h4>
                                    <p className="text-gray-300 leading-relaxed">
                                        Your DID can be instantly verified by <strong>50,000+ verification endpoints</strong> across 195 countries, supporting everything from airport security to online banking, employment verification to healthcare access.
                                    </p>
                                </div>
                            </div>

                            <Link to="/case-studies" className="btn-primary inline-flex items-center text-lg px-8 py-4">
                                Explore Trust Network
                                <ArrowRight size={24} className="ml-3" />
                            </Link>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Verifiable Credentials Section */}
            <section className="py-20 bg-surface-200/40">
                <div className="container-custom">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-4xl lg:text-5xl font-bold mb-6 relative">
                            <span className="bg-gradient-to-r from-white via-gray-100 to-white bg-clip-text text-transparent drop-shadow-lg">
                                Verifiable
                            </span>
                            <span className="text-neon-300"> Credentials</span>
                            <br />
                            <span className="bg-gradient-to-r from-neon-300 via-neon-400 to-primary-400 bg-clip-text text-transparent">
                                Digital Truth That Cannot Lie
                            </span>
                        </h2>
                        <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
                            Imagine a digital certificate so secure, so mathematically provable, that <strong className="text-white">lying becomes impossible</strong>. 
                            Welcome to Verifiable Credentials — the cryptographic revolution that transforms trust from faith into mathematical certainty.
                        </p>
                    </motion.div>

                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                        >
                            {/* Credential Visualization */}
                            <div className="relative">
                                <div className="absolute -inset-8 bg-gradient-to-r from-emerald-600/20 via-blue-600/20 to-purple-600/20 rounded-3xl blur-2xl"></div>
                                <div className="relative glass p-8 rounded-3xl border border-emerald-300/30">
                                    <div className="text-center mb-8">
                                        <h3 className="text-2xl font-bold text-white mb-3">The Anatomy of Trust</h3>
                                        <p className="text-emerald-300 font-medium">Cryptographically Signed Reality</p>
                                    </div>

                                    {/* Credential Flow Visualization */}
                                    <div className="space-y-8">
                                        {/* Issuer */}
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center space-x-4">
                                                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-2xl">
                                                    <GraduationCap className="w-8 h-8 text-white" />
                                                </div>
                                                <div>
                                                    <div className="text-lg font-bold text-white">ISSUER</div>
                                                    <div className="text-sm text-blue-300">University</div>
                                                    <div className="text-xs text-gray-400">DID: did:algo:mainnet:43df8a2...</div>
                                                </div>
                                            </div>
                                            <div className="flex-1 flex justify-center">
                                                <div className="text-center">
                                                    <div className="w-8 h-px bg-gradient-to-r from-blue-400 to-green-400 mb-1"></div>
                                                    <div className="text-xs text-green-400 font-semibold">SIGNS</div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Credential */}
                                        <div className="bg-gradient-to-br from-emerald-600/30 via-blue-600/30 to-purple-600/30 rounded-2xl p-6 border border-emerald-400/40">
                                            <div className="flex items-start justify-between mb-4">
                                                <div>
                                                    <div className="text-lg font-bold text-white mb-2">Computer Science Degree</div>
                                                    <div className="text-sm text-emerald-300 mb-1">Bachelor of Science</div>
                                                    <div className="text-xs text-gray-400">Issued: June 2024</div>
                                                </div>
                                                <div className="flex flex-col items-end">
                                                    <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center mb-2">
                                                        <CheckCircle className="w-6 h-6 text-green-400" />
                                                    </div>
                                                    <div className="text-xs text-green-400 font-bold">VERIFIED</div>
                                                </div>
                                            </div>
                                            
                                            <div className="bg-surface-200/40 rounded-lg p-3 mb-4">
                                                <div className="text-xs text-gray-400 mb-1">Cryptographic Signature</div>
                                                <div className="font-mono text-xs text-green-300 break-all">0x3c2f4a8b...9e7d1c5a</div>
                                            </div>

                                            <div className="grid grid-cols-2 gap-3">
                                                <div className="bg-surface-200/40 rounded-lg p-3">
                                                    <div className="text-xs text-gray-400">GPA</div>
                                                    <div className="text-white font-semibold">3.85</div>
                                                </div>
                                                <div className="bg-surface-200/40 rounded-lg p-3">
                                                    <div className="text-xs text-gray-400">Honor Roll</div>
                                                    <div className="text-emerald-300 font-semibold">Magna Cum Laude</div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Holder */}
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center space-x-4">
                                                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-2xl">
                                                    <User className="w-8 h-8 text-white" />
                                                </div>
                                                <div>
                                                    <div className="text-lg font-bold text-white">HOLDER</div>
                                                    <div className="text-sm text-purple-300">Alice Johnson</div>
                                                    <div className="text-xs text-gray-400">DID: did:algo:mainnet:84ba3c5...</div>
                                                </div>
                                            </div>
                                            <div className="flex-1 flex justify-center">
                                                <div className="text-center">
                                                    <div className="w-8 h-px bg-gradient-to-r from-purple-400 to-orange-400 mb-1"></div>
                                                    <div className="text-xs text-orange-400 font-semibold">PRESENTS</div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Verifier */}
                                        <div className="flex items-center">
                                            <div className="flex items-center space-x-4">
                                                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center shadow-2xl">
                                                    <Building2 className="w-8 h-8 text-white" />
                                                </div>
                                                <div>
                                                    <div className="text-lg font-bold text-white">VERIFIER</div>
                                                    <div className="text-sm text-orange-300">Company HR</div>
                                                    <div className="text-xs text-gray-400">Instant verification in &lt;1s</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6, delay: 0.4 }}
                            className="space-y-8"
                        >
                            <div>
                                <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
                                    <div className="w-3 h-8 bg-gradient-to-b from-emerald-400 to-blue-500 rounded-full mr-4"></div>
                                    The Trust Revolution
                                </h3>
                                <p className="text-lg text-gray-300 leading-relaxed mb-6">
                                    Traditional credentials are <strong className="text-white">easily forged</strong>, require manual verification, and depend on centralized authorities. 
                                    Verifiable Credentials use <strong className="text-emerald-300">military-grade cryptography</strong> to create digital certificates that are 
                                    mathematically impossible to fake, alter, or deny.
                                </p>
                                <p className="text-lg text-gray-300 leading-relaxed">
                                    Every credential is <strong className="text-blue-300">cryptographically signed</strong> by the issuer&apos;s blockchain-anchored DID, creating an 
                                    <strong className="text-purple-300"> unbreakable chain of trust</strong> that can be instantly verified anywhere in the world.
                                </p>
                            </div>

                            {/* Revolutionary Features */}
                            <div className="grid gap-6">
                                <div className="bg-gradient-to-r from-emerald-600/20 to-green-600/20 rounded-2xl p-6 border border-emerald-400/30">
                                    <h4 className="text-xl font-bold text-emerald-300 mb-4 flex items-center">
                                        <Shield className="w-6 h-6 mr-3" />
                                        Tamper-Proof Integrity
                                    </h4>
                                    <p className="text-gray-300 leading-relaxed mb-4">
                                        Once issued, credentials become <strong>cryptographically sealed</strong>. Any attempt to modify even a single character 
                                        breaks the digital signature, instantly revealing tampering attempts.
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        <span className="px-3 py-1 bg-emerald-500/20 text-emerald-300 rounded-full text-sm font-medium">Ed25519 Signatures</span>
                                        <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm font-medium">SHA-256 Hashing</span>
                                        <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm font-medium">Merkle Proofs</span>
                                    </div>
                                </div>

                                <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-2xl p-6 border border-blue-400/30">
                                    <h4 className="text-xl font-bold text-blue-300 mb-4 flex items-center">
                                        <Zap className="w-6 h-6 mr-3" />
                                        Instant Global Verification
                                    </h4>
                                    <p className="text-gray-300 leading-relaxed mb-4">
                                        No phone calls, no waiting weeks for background checks. Verifiers can instantly validate credentials 
                                        from <strong>anywhere in the world in under 1 second</strong> using only cryptographic mathematics.
                                    </p>
                                    <div className="grid grid-cols-3 gap-3 text-center">
                                        <div className="bg-surface-200/40 rounded-lg p-3">
                                            <div className="text-2xl font-bold text-blue-300">&lt;1s</div>
                                            <div className="text-xs text-gray-400">Verification Time</div>
                                        </div>
                                        <div className="bg-surface-200/40 rounded-lg p-3">
                                            <div className="text-2xl font-bold text-purple-300">99.99%</div>
                                            <div className="text-xs text-gray-400">Accuracy Rate</div>
                                        </div>
                                        <div className="bg-surface-200/40 rounded-lg p-3">
                                            <div className="text-2xl font-bold text-pink-300">24/7</div>
                                            <div className="text-xs text-gray-400">Availability</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-2xl p-6 border border-purple-400/30">
                                    <h4 className="text-xl font-bold text-purple-300 mb-4 flex items-center">
                                        <Key className="w-6 h-6 mr-3" />
                                        Selective Disclosure Magic
                                    </h4>
                                    <p className="text-gray-300 leading-relaxed mb-4">
                                        Share only what you need to share. Prove you&apos;re over 21 without revealing your exact age. 
                                        Prove you have a degree without sharing your GPA. <strong>Privacy-preserving by design.</strong>
                                    </p>
                                    <div className="bg-surface-200/40 rounded-lg p-4">
                                        <div className="text-sm text-gray-400 mb-2">Example: Age Verification</div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <div className="text-xs text-red-400 font-semibold mb-1">❌ Traditional</div>
                                                <div className="text-xs text-gray-300">Share: Full DOB, Name, Address</div>
                                            </div>
                                            <div>
                                                <div className="text-xs text-green-400 font-semibold mb-1">✅ Verifiable</div>
                                                <div className="text-xs text-gray-300">Share: &ldquo;Age &gt; 21&rdquo; (Cryptographically Proven)</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Real-World Impact */}
                            <div className="bg-gradient-to-r from-neon-600/20 to-primary-600/20 rounded-2xl p-6 border border-neon-300/30">
                                <h4 className="text-xl font-bold text-neon-300 mb-4">Real-World Revolution</h4>
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="space-y-3">
                                        <div className="flex items-start space-x-3">
                                            <GraduationCap className="w-5 h-5 text-blue-400 mt-1 flex-shrink-0" />
                                            <div>
                                                <div className="text-white font-semibold text-sm">Education</div>
                                                <div className="text-xs text-gray-400">Instant degree verification, eliminating diploma mills</div>
                                            </div>
                                        </div>
                                        <div className="flex items-start space-x-3">
                                            <Building2 className="w-5 h-5 text-green-400 mt-1 flex-shrink-0" />
                                            <div>
                                                <div className="text-white font-semibold text-sm">Employment</div>
                                                <div className="text-xs text-gray-400">Verified work history and skills, no more resume fraud</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="space-y-3">
                                        <div className="flex items-start space-x-3">
                                            <Shield className="w-5 h-5 text-purple-400 mt-1 flex-shrink-0" />
                                            <div>
                                                <div className="text-white font-semibold text-sm">Healthcare</div>
                                                <div className="text-xs text-gray-400">Secure medical records and vaccination proof</div>
                                            </div>
                                        </div>
                                        <div className="flex items-start space-x-3">
                                            <Award className="w-5 h-5 text-orange-400 mt-1 flex-shrink-0" />
                                            <div>
                                                <div className="text-white font-semibold text-sm">Licenses</div>
                                                <div className="text-xs text-gray-400">Driver&apos;s licenses, certifications, and permits</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <Link to="/pricing" className="btn-primary inline-flex items-center text-lg px-8 py-4">
                                Issue Your First Credential
                                <ArrowRight size={24} className="ml-3" />
                            </Link>
                        </motion.div>
                    </div>
                </div>
            </section>


            {/* Call to Action */}
            <section className="py-20 bg-gradient-to-r from-primary-600 to-neon-600">
                <div className="container-custom text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                            Ready to Build with Documint?
                        </h2>
                        <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                            Join hundreds of organizations already using our platform to power their identity infrastructure.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <a href="https://app.passhub.documint.id/signup" target="_blank" rel="noopener noreferrer" className="bg-white text-primary-600 hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-colors duration-200 inline-flex items-center">
                                Start Free Trial
                                <ArrowRight size={20} className="ml-2" />
                            </a>
                            <Link to="/docs" className="border border-white/20 text-white hover:bg-white/10 px-8 py-3 rounded-lg font-semibold transition-colors duration-200 inline-flex items-center">
                                View Documentation
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default TechPlatform;